import { BaseApiClient } from './base-client';
import type { ApiResponse, VideoGenerationParams, GeneratedVideo } from '../../types';

/**
 * Video API - методы для генерации и управления видео
 */
export class VideoApi extends BaseApiClient {
  
  // ===== VIDEO GENERATION API =====
  
  /**
   * Генерация видео
   */
  async generateVideo(params: VideoGenerationParams): Promise<ApiResponse<{ videos: GeneratedVideo[]; newBalance?: number }>> {
    try {
      // Создаем FormData для отправки файлов
      const formData = new FormData();
      
      // Добавляем обычные параметры
      formData.append('prompt', params.prompt);
      formData.append('provider', params.provider);
      formData.append('model', params.model);
      
      if (params.quality) formData.append('quality', params.quality);
      if (params.mode) formData.append('mode', params.mode); // Kling: std/pro mode
      if (params.duration) formData.append('duration', params.duration);
      if (params.ratio) formData.append('ratio', params.ratio);
      if (params.enhancePrompt !== undefined) formData.append('enhancePrompt', String(params.enhancePrompt));
      if (params.autoTranslate !== undefined) formData.append('autoTranslate', String(params.autoTranslate));
      if (params.seed) formData.append('seed', String(params.seed));
      
      // Audio параметр для Veo 3/3.1 моделей и Kling 3.0
      if (params.generateAudio !== undefined) {
        formData.append('generateAudio', String(params.generateAudio));
        console.log('[VideoApi] generateAudio appended to FormData:', String(params.generateAudio));
      } else {
        console.log('[VideoApi] generateAudio is undefined, NOT appending');
      }
      
      // Добавляем motions как JSON string
      if (params.motions && params.motions.length > 0) {
        formData.append('motions', JSON.stringify(params.motions));
      }
      
      // Добавляем frame S3 URLs (файлы уже загружены в S3)
      if (params.frames && params.frames.length > 0) {
        params.frames.forEach((frame) => {
          // Если кадр уже загружен в S3, отправляем только URL
          if (frame.s3Key && !frame.s3Key.startsWith('temp-') && frame.dataUrl) {
            if (frame.type === 'start' || frame.type === 'actTwoCharacter') {
              // actTwoCharacter отправляется как startFrame на бэкенд
              formData.append('startFrameUrl', frame.dataUrl);
              formData.append('startFrameS3Key', frame.s3Key);
            } else if (frame.type === 'end') {
              formData.append('endFrameUrl', frame.dataUrl);
              formData.append('endFrameS3Key', frame.s3Key);
            } else if (frame.type === 'referenceVideo') {
              // Act Two: референсное видео
              formData.append('referenceVideoUrl', frame.dataUrl);
              formData.append('referenceVideoS3Key', frame.s3Key);
              // Передаём длительность для расчёта цены
              if ((frame as any).videoDuration) {
                formData.append('referenceVideoDuration', String((frame as any).videoDuration));
              }
            }
          } else if (frame.file) {
            // Fallback: если файл еще не загружен, отправляем сам файл
            if (frame.type === 'start' || frame.type === 'actTwoCharacter') {
              formData.append('startFrame', frame.file);
            } else if (frame.type === 'end') {
              formData.append('endFrame', frame.file);
            } else if (frame.type === 'referenceVideo') {
              formData.append('referenceVideo', frame.file);
            }
          }
        });
      }
      
      // Добавляем референсные изображения (для Veo 3.1)
      if (params.referenceImages && params.referenceImages.length > 0) {
        params.referenceImages.forEach((refImage, index) => {
          // Если изображение уже загружено в S3, отправляем URL и S3 Key
          if (refImage.s3Key && !refImage.s3Key.startsWith('temp-') && refImage.dataUrl) {
            formData.append(`referenceImageUrl${index + 1}`, refImage.dataUrl);
            formData.append(`referenceImageS3Key${index + 1}`, refImage.s3Key);
          } else if (refImage.file) {
            formData.append(`referenceImage${index + 1}`, refImage.file);
          }
        });
        
        // Добавляем количество референсных изображений
        formData.append('referenceImagesCount', params.referenceImages.length.toString());
      }
      
      // Seedance 2.0: Reference videos (with per-ref trim)
      if (params.referenceVideos && params.referenceVideos.length > 0) {
        params.referenceVideos.forEach((refVideo: any, index: number) => {
          if (refVideo.dataUrl && refVideo.s3Key) {
            formData.append(`referenceVideoUrl${index + 1}`, refVideo.dataUrl);
            formData.append(`referenceVideoS3Key${index + 1}`, refVideo.s3Key);
            if (refVideo.trimStart != null) formData.append(`referenceVideoTrimStart${index + 1}`, String(refVideo.trimStart));
            if (refVideo.trimEnd != null) formData.append(`referenceVideoTrimEnd${index + 1}`, String(refVideo.trimEnd));
          }
        });
        formData.append('referenceVideosCount', params.referenceVideos.length.toString());
      }

      // Seedance 2.0: Reference audios (with per-ref trim)
      if (params.referenceAudios && params.referenceAudios.length > 0) {
        params.referenceAudios.forEach((refAudio: any, index: number) => {
          if (refAudio.dataUrl && refAudio.s3Key) {
            formData.append(`referenceAudioUrl${index + 1}`, refAudio.dataUrl);
            formData.append(`referenceAudioS3Key${index + 1}`, refAudio.s3Key);
            if (refAudio.trimStart != null) formData.append(`referenceAudioTrimStart${index + 1}`, String(refAudio.trimStart));
            if (refAudio.trimEnd != null) formData.append(`referenceAudioTrimEnd${index + 1}`, String(refAudio.trimEnd));
          }
        });
        formData.append('referenceAudiosCount', params.referenceAudios.length.toString());
      }

      // Kling 3.0 Multi-Shot
      if (params.multiPrompt && params.multiPrompt.length > 0) {
        const multiPromptJson = JSON.stringify(params.multiPrompt);
        formData.append('multiPrompt', multiPromptJson);
        console.log('[VideoApi] multiPrompt appended to FormData:', multiPromptJson);
      } else {
        console.log('[VideoApi] multiPrompt is empty/undefined:', params.multiPrompt);
      }

      // Kling 3.0 Elements (reference images for characters)
      if (params.elements && params.elements.length > 0) {
        formData.append('elements', JSON.stringify(params.elements));
      }

      // Kling O1 Video-to-Video: добавляем входное видео
      if (params.inputVideo && params.inputVideo.s3Key && params.inputVideo.dataUrl) {
        formData.append('inputVideoUrl', params.inputVideo.dataUrl);
        formData.append('inputVideoS3Key', params.inputVideo.s3Key);
        // Используем inputVideoDuration из params или из самого видео
        const videoDuration = params.inputVideoDuration ?? params.inputVideo.duration;
        if (videoDuration !== null && videoDuration !== undefined) {
          formData.append('inputVideoDuration', String(videoDuration));
        }
      }

      // V2V trim range (server-side ffmpeg trim before sending to provider)
      if (params.trimStart != null) formData.append('trimStart', String(params.trimStart));
      if (params.trimEnd != null) formData.append('trimEnd', String(params.trimEnd));

      // Kling Camera Control
      if (params.cameraControl) {
        formData.append('cameraControl', JSON.stringify(params.cameraControl));
      }

      // Kling O1/O3 v2v: keep original sound
      if (params.keepOriginalSound !== undefined) {
        formData.append('keepOriginalSound', params.keepOriginalSound ? 'yes' : 'no');
      }

      // Grok v2v: sub-mode ('edit' or 'extend')
      if ((params as any).v2vMode) {
        formData.append('v2vMode', String((params as any).v2vMode));
      }

      // Team mode параметры
      if (params.teamId) formData.append('teamId', params.teamId);
      if (params.folderId) formData.append('folderId', params.folderId);

      const response = await this.client.post('/api/video/generate', formData);

      return response.data;
    } catch (error) {
      console.error('Video generation error:', error);
      throw error;
    }
  }

  /**
   * Retry a failed video generation using stored error card params.
   * Sends raw S3 URLs directly (no File uploads) to the same /api/video/generate endpoint.
   */
  async retryVideoGeneration(params: {
    prompt: string;
    provider: string;
    model: string;
    quality?: string;
    duration?: string;
    ratio?: string;
    seed?: number | string;
    mode?: string;
    generateAudio?: boolean;
    autoTranslate?: boolean;
    startFrameUrl?: string;
    startFrameS3Key?: string;
    endFrameUrl?: string;
    endFrameS3Key?: string;
    referenceImages?: Array<{ url: string; s3Key?: string }>;
    referenceVideos?: Array<{ url: string; s3Key?: string }>;
    referenceAudios?: Array<{ url: string; s3Key?: string }>;
    motions?: any[];
    elements?: any[];
    multiPrompt?: any[];
    inputVideoUrl?: string;
    inputVideoS3Key?: string;
    inputVideoDuration?: number;
    trimStart?: number;
    trimEnd?: number;
    cameraControl?: any;
    keepOriginalSound?: string;
    v2vMode?: 'edit' | 'extend';
    teamId?: string;
    folderId?: string;
  }): Promise<ApiResponse<{ videos: GeneratedVideo[]; newBalance?: number }>> {
    const formData = new FormData();
    formData.append('prompt', params.prompt);
    formData.append('provider', params.provider);
    formData.append('model', params.model);

    if (params.quality) formData.append('quality', params.quality);
    if (params.mode) formData.append('mode', params.mode);
    if (params.duration) formData.append('duration', params.duration);
    if (params.ratio) formData.append('ratio', params.ratio);
    if (params.seed) formData.append('seed', String(params.seed));
    if (params.autoTranslate) formData.append('autoTranslate', 'true');
    if (params.generateAudio !== undefined) formData.append('generateAudio', String(params.generateAudio));

    if (params.startFrameUrl) {
      formData.append('startFrameUrl', params.startFrameUrl);
      if (params.startFrameS3Key) formData.append('startFrameS3Key', params.startFrameS3Key);
    }
    if (params.endFrameUrl) {
      formData.append('endFrameUrl', params.endFrameUrl);
      if (params.endFrameS3Key) formData.append('endFrameS3Key', params.endFrameS3Key);
    }

    if (params.referenceImages?.length) {
      params.referenceImages.forEach((ref, idx) => {
        formData.append(`referenceImageUrl${idx + 1}`, ref.url);
        if (ref.s3Key) formData.append(`referenceImageS3Key${idx + 1}`, ref.s3Key);
      });
      formData.append('referenceImagesCount', String(params.referenceImages.length));
    }

    // Seedance 2.0: Reference videos
    if (params.referenceVideos?.length) {
      params.referenceVideos.forEach((ref: any, idx: number) => {
        if (ref.url) formData.append(`referenceVideoUrl${idx + 1}`, ref.url);
        if (ref.s3Key) formData.append(`referenceVideoS3Key${idx + 1}`, ref.s3Key);
      });
      formData.append('referenceVideosCount', String(params.referenceVideos.length));
    }
    // Seedance 2.0: Reference audios
    if (params.referenceAudios?.length) {
      params.referenceAudios.forEach((ref: any, idx: number) => {
        if (ref.url) formData.append(`referenceAudioUrl${idx + 1}`, ref.url);
        if (ref.s3Key) formData.append(`referenceAudioS3Key${idx + 1}`, ref.s3Key);
      });
      formData.append('referenceAudiosCount', String(params.referenceAudios.length));
    }

    if (params.motions?.length) formData.append('motions', JSON.stringify(params.motions));
    if (params.elements?.length) formData.append('elements', JSON.stringify(params.elements));
    if (params.multiPrompt?.length) formData.append('multiPrompt', JSON.stringify(params.multiPrompt));

    if (params.inputVideoUrl) {
      formData.append('inputVideoUrl', params.inputVideoUrl);
      if (params.inputVideoS3Key) formData.append('inputVideoS3Key', params.inputVideoS3Key);
      if (params.inputVideoDuration) formData.append('inputVideoDuration', String(params.inputVideoDuration));
    }

    if (params.trimStart != null) formData.append('trimStart', String(params.trimStart));
    if (params.trimEnd != null) formData.append('trimEnd', String(params.trimEnd));
    if (params.cameraControl) formData.append('cameraControl', JSON.stringify(params.cameraControl));
    if (params.keepOriginalSound) formData.append('keepOriginalSound', params.keepOriginalSound);
    if (params.v2vMode) formData.append('v2vMode', params.v2vMode);
    if (params.teamId) formData.append('teamId', params.teamId);
    if (params.folderId) formData.append('folderId', params.folderId);

    const response = await this.client.post('/api/video/generate', formData);
    return response.data;
  }

  /**
   * Получить список доступных motion presets для Higgsfield
   */
  async getMotionPresets(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.client.get('/api/video/motions');
      return response.data;
    } catch (error) {
      console.error('Error fetching motion presets:', error);
      throw error;
    }
  }

  // ===== JOB STATUS POLLING =====

  /**
   * Get video generation job status (BullMQ polling)
   */
  async getVideoJobStatus(jobId: string): Promise<ApiResponse<{
    jobId: string;
    status: 'waiting' | 'active' | 'completed' | 'failed';
    result?: any;
    error?: string;
    generationId?: string;
    createdAt?: number;
    finishedAt?: number;
  }>> {
    const response = await this.client.get(`/api/video/job/${jobId}/status`);
    return response.data;
  }

  // ===== SORA VIDEO API =====
  
  /**
   * Remix существующего Sora видео с новым промптом
   */
  async remixSoraVideo(videoId: string, newPrompt: string): Promise<ApiResponse<{ video: GeneratedVideo }>> {
    try {
      const response = await this.client.post('/api/video/sora/remix', {
        videoId,
        newPrompt
      });
      return response.data;
    } catch (error) {
      console.error('Error remixing Sora video:', error);
      throw error;
    }
  }

  /**
   * Получить список Sora видео
   */
  async listSoraVideos(options?: { limit?: number; after?: string; order?: string }): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.after) params.append('after', options.after);
      if (options?.order) params.append('order', options.order);

      const response = await this.client.get(`/api/video/sora/list?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error listing Sora videos:', error);
      throw error;
    }
  }

  /**
   * Удалить Sora видео
   */
  async deleteSoraVideo(videoId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.delete(`/api/video/sora/${videoId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting Sora video:', error);
      throw error;
    }
  }

  /**
   * Скачать Sora видео или его варианты
   */
  async downloadSoraVideo(videoId: string, variant: 'video' | 'thumbnail' | 'spritesheet' = 'video'): Promise<Blob> {
    try {
      const response = await this.client.get(`/api/video/sora/${videoId}/download`, {
        params: { variant },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading Sora video:', error);
      throw error;
    }
  }

  // ===== VIDEO HISTORY & MANAGEMENT =====

  /**
   * Загрузить историю видео пользователя с пагинацией
   */
  async loadVideoHistory(
    limit: number = 50, 
    teamId?: string, 
    folderId?: string, 
    memberFilter?: string,
    offset: number = 0
  ): Promise<ApiResponse<{ videos: any[]; count: number; hasMore: boolean; offset: number; limit: number }>> {
    try {
      const params: Record<string, any> = { limit, offset };
      if (teamId) params.teamId = teamId;
      if (folderId) params.folderId = folderId;
      if (memberFilter) params.memberFilter = memberFilter;
      
      const response = await this.client.get('/api/video/history', { params });
      return response.data;
    } catch (error) {
      console.error('Error loading video history:', error);
      throw error;
    }
  }

  /**
   * Удалить видео из базы данных и S3
   */
  async deleteVideo(videoId: string): Promise<ApiResponse<{ videoId: string; s3Deleted: boolean; dbDeleted: boolean }>> {
    try {
      const response = await this.client.delete(`/api/video/${videoId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  }

  /**
   * Пометить/снять пометку видео как избранное
   */
  async toggleVideoFavorite(videoId: string, isFavorite: boolean): Promise<ApiResponse<{ videoId: string; isFavorite: boolean }>> {
    try {
      const response = await this.client.post(`/api/video/${videoId}/favorite`, { isFavorite });
      return response.data;
    } catch (error) {
      console.error('Error toggling video favorite:', error);
      throw error;
    }
  }

  /**
   * Получить избранные видео пользователя
   */
  async getFavoriteVideos(limit: number = 50): Promise<ApiResponse<{ videos: any[]; count: number }>> {
    try {
      const response = await this.client.get(`/api/video/favorites?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error loading favorite videos:', error);
      throw error;
    }
  }

  // ===== ACTIVE GENERATIONS =====

  /**
   * Получить список активных генераций видео
   * В team mode возвращает генерации всех участников команды
   * @param teamId - ID команды для team mode (опционально)
   */
  async getActiveVideoGenerations(teamId?: string): Promise<ApiResponse<{ generations: any[]; count: number; isTeamMode?: boolean }>> {
    try {
      const params: Record<string, string> = {};
      if (teamId) params.teamId = teamId;
      
      const response = await this.client.get('/api/video/active-generations', { params });
      return response.data;
    } catch (error) {
      console.error('Error loading active video generations:', error);
      throw error;
    }
  }

  /**
   * Отменить активную генерацию видео
   */
  async cancelVideoGeneration(generationId: string): Promise<ApiResponse<{ generationId: string; cancelled: boolean }>> {
    try {
      const response = await this.client.delete(`/api/video/active-generations/${generationId}`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling video generation:', error);
      throw error;
    }
  }

  /**
   * Очистить все застрявшие активные генерации видео
   */
  async clearStuckVideoGenerations(): Promise<ApiResponse<{ cleared: number; generationIds: string[] }>> {
    try {
      const response = await this.client.post('/api/video/active-generations/clear');
      return response.data;
    } catch (error) {
      console.error('Error clearing stuck video generations:', error);
      throw error;
    }
  }
}

